/**
 * Utah's state pack. Every fact here was already in the app before the packs (2026-10-02) and keeps its source: the
 * research files named on each row (docs/source/research/*). Moving them here changes no behaviour (the Utah suites pin it).
 *
 * Utah adopts the 2022 FDA Food Code by reference (R392-100-4(1)(b)), with the December 2024 Supplement since 6/11/2025.
 * The local layer (13 health departments, their county cells and the state-layer checks) is ut-local.ts.
 */
import type { StatePack } from "./types.ts";

const D = "2026-09-29"; // state/county research pass (utah-county-rules.md)

const R392_100 = "https://adminrules.utah.gov/public/rule/R392-100/Current%20Rules";
const UOSH_RULE = "https://www.law.cornell.edu/regulations/utah/Utah-Admin-Code-R614-1-5";

/** The 29 counties, A to Z (the 13 departments' county lists in ut-local.ts; a test checks they match). */
const COUNTIES = [
  "Beaver", "Box Elder", "Cache", "Carbon", "Daggett", "Davis", "Duchesne", "Emery", "Garfield", "Grand", "Iron", "Juab", "Kane", "Millard",
  "Morgan", "Piute", "Rich", "Salt Lake", "San Juan", "Sanpete", "Sevier", "Summit", "Tooele", "Uintah", "Utah", "Wasatch", "Washington",
  "Wayne", "Weber",
] as const;

export const UT: StatePack = {
  code: "UT",
  name: "Utah",
  slug: "utah",
  timezone: "America/Denver",
  foodCode: {
    edition: "2022",
    rule: "Utah Admin. Code R392-100",
    adoptsByReference: "R392-100-4(1)(b)",
    text: "Utah follows the 2022 FDA Food Code (Utah Admin. Code R392-100). A county can ask for more, so ask yours.",
    source: {
      citation: "Utah Admin. Code R392-100-4(1)(b)",
      url: R392_100,
      quote: "The 2022 version of the U.S. Food and Drug Administration Food Code (Food Code), Chapters 1 through 8",
      lastChecked: D,
    },
    // Utah adopts the Food Code text itself: every section is Utah's, numbered the same (amendments in R392-100-4(3)).
    mapped: {},
  },
  stateAgency: {
    id: "ut-dhhs",
    name: "Utah Department of Health and Human Services",
    short: "Utah DHHS",
    url: "https://epi.utah.gov/wp-content/uploads/foodHandler_list.pdf",
    phones: [],
  },
  counties: COUNTIES,
  permits: ["food_establishment", "temporary_event", "mehk", "private_members_only", "mobile_food_business"],
  foodHandler: {
    status: "verified",
    text: "Food handlers finish an approved course within 14 days of starting and get a county permit within 30 days. Valid 3 years, anywhere in Utah.",
    deadline: "course by day 14, permit by day 30",
    source: { citation: "Utah Code 26B-7-413; R392-103", url: "https://le.utah.gov/xcode/Title26B/Chapter7/26B-7-S413.html", lastChecked: D },
  },
  manager: {
    status: "verified",
    text: "At least one supervisor is a certified food safety manager. Renew every 5 years (Utah Code 26B-7-412).",
    source: { citation: "Utah Code 26B-7-410, 26B-7-412", url: "https://le.utah.gov/xcode/Title26B/Chapter7/26B-7-S412.html", lastChecked: D },
  },
  illnessReporting: {
    status: "verified",
    text: "The person in charge reports a diagnosed foodborne illness to the county health department.",
    source: { citation: "Food Code 2-201.11; 8-501.10(C) (Utah amendment: illness reporting under R386-702 and R386-703)", url: R392_100, lastChecked: D },
  },
  workplace: {
    plan: "state",
    agency: {
      id: "uosh",
      name: "Utah Occupational Safety and Health (UOSH)",
      short: "Utah OSHA (UOSH)",
      url: UOSH_RULE,
      phones: [{ label: "UOSH", number: "(801) 530-6901", source: { citation: "Utah Admin. Code R614-1-5(B)", url: UOSH_RULE, quote: "Call (801) 530-6901.", lastChecked: "2026-09-30" } }],
    },
    reportLine: "Utah law: if the injury is serious, the owner or person in charge tells Utah OSHA (UOSH) within 8 hours.",
    askClause: "the person in charge tells Utah OSHA within 8 hours",
    citation: "Utah Admin. Code R614-1-5(B)",
    source: {
      citation: "Utah Admin. Code R614-1-5(B)",
      url: UOSH_RULE,
      quote: "Each employer shall within 8 hours of occurrence, notify UOSH of any work-related fatalities, of any disabling, serious, or significant injury and of any occupational disease incident.",
      lastChecked: "2026-09-30",
    },
  },
  mobile: {
    status: "verified",
    text: "One annual permit from the health department where most of your operating happens, honored by every Utah health department. A signed commissary agreement, with each visit logged and kept a year.",
    source: { citation: "Utah Code 11-56; R392-102-3", url: "https://www.law.cornell.edu/regulations/utah/Utah-Admin-Code-R392-102-3", lastChecked: D },
  },
  cottageFood: { status: "unknown", tried: "Not researched in this app: Utah cottage food is licensed by the Utah Department of Agriculture and Food, and no rule text is saved in docs/source.", lastChecked: "2026-10-02" },
  homeKitchen: {
    status: "verified",
    text: "A microenterprise home kitchen needs a permit from your local health department, a food handler permit and a certified food safety manager.",
    source: { citation: "Utah Code 26B-7-416; R392-106-4", url: "https://le.utah.gov/xcode/Title26B/Chapter7/26B-7-S416.html", lastChecked: D },
  },
  postings: [
    { id: "handwashing-sign", what: "A sign at every handwashing sink telling workers to wash their hands.", who: "every food business", source: { citation: "Food Code 6-301.14, adopted by R392-100-4(1)(b)", url: R392_100, lastChecked: D } },
  ],
  inspections: [],
  rules: {},
  copy: {
    showing: "Showing Utah rules.",
    notHere: "Not in Utah? Pick your state.",
    lawLabel: "Utah law",
    sameAsState: "same as Utah law",
    foodCodeLine: "Utah follows the 2022 FDA Food Code (Utah Admin. Code R392-100).",
    verdictLine: "Utah follows the 2022 Food Code (Utah Admin. Code R392-100), so this is Utah's rule. Your county can ask for more, so ask them.",
  },
  lastChecked: D,
  research: "docs/source/research/utah-county-rules.md",
};
